import {FC, useCallback, useState, useEffect} from "react";
import type {Dayjs} from "dayjs";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import {CourseEnrollmentDueDateDialog} from "./components/CourseEnrollmentDueDateDialog";
import {useCourseEnrollmentActions} from "./hooks/useCourseEnrollmentActions";

interface IProps {
	open: boolean;
	onClose: () => void;
	selectedUserIds: number[];
	courseId: number;
}

export const BulkEnrollmentConfirmationModal: FC<IProps> = ({open, onClose, selectedUserIds, courseId}) => {
	const {t} = useSSTranslation();
	const {dueDateAssignments} = useDevCycleSdk();
	const {enrollUsers, isBulkPending} = useCourseEnrollmentActions(courseId);
	const [dueDate, setDueDate] = useState<Dayjs | null>(null);

	useEffect(() => {
		if (!open) {
			setDueDate(null);
		}
	}, [open]);

	const handleApply = useCallback(() => {
		if (!open) {
			return;
		}

		enrollUsers(
			{
				userIds: selectedUserIds,
				dueDate,
				showDueDateSection: dueDateAssignments
			},
			{onSuccess: onClose}
		);
	}, [open, selectedUserIds, dueDate, dueDateAssignments, enrollUsers, onClose]);

	return (
		<CourseEnrollmentDueDateDialog
			open={open}
			onClose={onClose}
			onApply={handleApply}
			isPending={isBulkPending}
			showDueDateSection={dueDateAssignments}
			dueDate={dueDate}
			onDueDateChange={setDueDate}
			titleId="alert-dialog-title"
			descriptionId="alert-dialog-description"
			title={t("bulk_enrollment_modal_title", "Enroll Users in Course", {count: selectedUserIds.length})}
			description={t(
				"bulk_enrollment_modal_body_copy",
				"Enroll selected learners in this course.",
				{count: selectedUserIds.length}
			)}
		/>
	);
};
